/**
 * main-collection-filters.js
 * Drop this file in: assets/main-collection-filters.js
 *
 * Handles, for one collection filter section:
 *  - checkbox / boolean filters (native Shopify collection.filters)
 *  - price range filter
 *  - sort dropdown
 *  - pagination
 *  - instant client-side search box (filters the currently loaded grid by title)
 *
 * All of the above (except search) re-fetch the section via Shopify's
 * Section Rendering API (?section_id=...) so the grid updates without a
 * full page reload, and the URL updates via history.pushState so the
 * filtered view is shareable and back/forward works.
 */
(function () {
  'use strict';

  function CollectionFilters(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.collectionUrl = root.dataset.collectionUrl;
    this.grid = root.querySelector('[data-cf-grid]');
    this.searchInput = root.querySelector('.cf-search-input');
    this.searchClear = root.querySelector('.cf-search-clear');

    this.bindFilterInputs();
    this.bindPriceFilter();
    this.bindSort();
    this.bindAjaxLinks();
    this.bindSearch();
    this.bindMobileDrawer();
    this.bindGroupToggles();
    this.updateActiveCount();

    // support back/forward navigation
    window.addEventListener('popstate', function () {
      this.fetchAndRender(window.location.href, false);
    }.bind(this));
  }

  CollectionFilters.prototype.currentParams = function () {
    return new URLSearchParams(window.location.search);
  };

  /* ---------------- checkbox / boolean filters ---------------- */
  CollectionFilters.prototype.bindFilterInputs = function () {
    var self = this;
    this.root.querySelectorAll('[data-cf-filter-input]').forEach(function (input) {
      input.addEventListener('change', function () {
        var url = input.checked ? input.dataset.addUrl : input.dataset.removeUrl;
        if (url) self.fetchAndRender(url, true);
      });
    });
  };

  /* ---------------- price range filter ---------------- */
  CollectionFilters.prototype.bindPriceFilter = function () {
    var self = this;
    this.root.querySelectorAll('[data-cf-price-filter]').forEach(function (wrap) {
      var applyBtn = wrap.querySelector('[data-cf-price-apply]');
      var minInput = wrap.querySelector('[data-price-min]');
      var maxInput = wrap.querySelector('[data-price-max]');
      var paramMin = wrap.dataset.paramMin;
      var paramMax = wrap.dataset.paramMax;

      applyBtn.addEventListener('click', function () {
        var params = self.currentParams();
        var minVal = parseFloat(minInput.value);
        var maxVal = parseFloat(maxInput.value);

        if (!isNaN(minVal) && minVal > 0) {
          params.set(paramMin, Math.round(minVal * 100));
        } else {
          params.delete(paramMin);
        }
        if (!isNaN(maxVal) && maxVal > 0) {
          params.set(paramMax, Math.round(maxVal * 100));
        } else {
          params.delete(paramMax);
        }
        params.delete('page');
        var url = self.collectionUrl + '?' + params.toString();
        self.fetchAndRender(url, true);
      });
    });
  };

  /* ---------------- sort dropdown ---------------- */
  CollectionFilters.prototype.bindSort = function () {
    var self = this;
    var select = this.root.querySelector('.cf-sort-select');
    if (!select) return;
    select.addEventListener('change', function () {
      var params = self.currentParams();
      params.set('sort_by', select.value);
      var url = self.collectionUrl + '?' + params.toString();
      self.fetchAndRender(url, true);
    });
  };

  /* ---------------- pagination + chip links ---------------- */
  CollectionFilters.prototype.bindAjaxLinks = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var link = e.target.closest('[data-cf-ajax-link]');
      if (!link) return;
      e.preventDefault();
      self.fetchAndRender(link.href, true);
    });
  };

  /* ---------------- instant search (client-side, current grid) ---------------- */
  CollectionFilters.prototype.bindSearch = function () {
    var self = this;
    if (!this.searchInput) return;

    var run = function () {
      var term = self.searchInput.value.trim().toLowerCase();
      self.searchClear.hidden = term.length === 0;

      var cards = self.grid.querySelectorAll('.cf-card');
      var visibleCount = 0;
      cards.forEach(function (card) {
        var title = (card.dataset.cfTitle || '').toLowerCase();
        var match = term === '' || title.indexOf(term) !== -1;
        card.dataset.cfHidden = match ? 'false' : 'true';
        if (match) visibleCount++;
      });

      var countEl = self.root.querySelector('[id^="CF-ResultCount-"]');
      if (countEl) {
        countEl.textContent = term
          ? visibleCount + ' result' + (visibleCount !== 1 ? 's' : '') + ' for "' + self.searchInput.value.trim() + '"'
          : self.grid.querySelectorAll('.cf-card').length + ' products';
      }
    };

    var debounced;
    this.searchInput.addEventListener('input', function () {
      clearTimeout(debounced);
      debounced = setTimeout(run, 150);
    });

    this.searchClear.addEventListener('click', function () {
      self.searchInput.value = '';
      run();
      self.searchInput.focus();
    });
  };

  /* ---------------- mobile drawer ---------------- */
  CollectionFilters.prototype.bindMobileDrawer = function () {
    var openBtn = this.root.querySelector('[id^="CF-OpenFilters-"]');
    var closeBtn = this.root.querySelector('[id^="CF-CloseFilters-"]');
    var sidebar = this.root.querySelector('[id^="CF-Sidebar-"]');
    var overlay = this.root.querySelector('[id^="CF-Overlay-"]');
    if (!openBtn || !sidebar) return;

    function open() {
      sidebar.dataset.open = 'true';
      overlay.dataset.open = 'true';
      document.body.style.overflow = 'hidden';
    }
    function close() {
      sidebar.dataset.open = 'false';
      overlay.dataset.open = 'false';
      document.body.style.overflow = '';
    }
    openBtn.addEventListener('click', open);
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', close);
  };

  /* ---------------- collapsible filter groups ---------------- */
  CollectionFilters.prototype.bindGroupToggles = function () {
    this.root.querySelectorAll('.cf-filter-group-toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var group = btn.closest('.cf-filter-group');
        var collapsed = group.dataset.collapsed === 'true';
        group.dataset.collapsed = collapsed ? 'false' : 'true';
        btn.setAttribute('aria-expanded', String(collapsed));
      });
    });
  };

  CollectionFilters.prototype.updateActiveCount = function () {
    var count = this.root.querySelectorAll('[data-cf-filter-input]:checked').length;
    var badge = this.root.querySelector('[id^="CF-ActiveCount-"]');
    if (!badge) return;
    if (count > 0) {
      badge.hidden = false;
      badge.textContent = count;
    } else {
      badge.hidden = true;
    }
  };

  /* ---------------- core: fetch section HTML, swap it in ---------------- */
  CollectionFilters.prototype.fetchAndRender = function (url, pushState) {
    var self = this;
    var fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    this.grid.classList.add('cf-loading');

    fetch(fetchUrl.toString())
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newRoot = doc.getElementById(self.root.id);
        if (!newRoot) return;

        self.root.innerHTML = newRoot.innerHTML;

        if (pushState) {
          var cleanUrl = new URL(url, window.location.origin);
          cleanUrl.searchParams.delete('section_id');
          window.history.pushState({}, '', cleanUrl.pathname + cleanUrl.search);
        }

        // re-bind everything against the freshly swapped-in markup
        self.grid = self.root.querySelector('[data-cf-grid]');
        self.searchInput = self.root.querySelector('.cf-search-input');
        self.searchClear = self.root.querySelector('.cf-search-clear');
        self.bindFilterInputs();
        self.bindPriceFilter();
        self.bindSort();
        self.bindSearch();
        self.bindGroupToggles();
        self.updateActiveCount();

        var sidebar = self.root.querySelector('[id^="CF-Sidebar-"]');
        var overlay = self.root.querySelector('[id^="CF-Overlay-"]');
        if (sidebar) sidebar.dataset.open = 'false';
        if (overlay) overlay.dataset.open = 'false';
        document.body.style.overflow = '';

        window.scrollTo({ top: self.root.offsetTop - 20, behavior: 'smooth' });
      })
      .catch(function (err) {
        console.error('Collection filter fetch failed:', err);
        self.grid.classList.remove('cf-loading');
      });
  };

  window.CollectionFilters = CollectionFilters;
})();
